
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle } from "lucide-react";

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
  isDeleting?: boolean;
  title: string;
  description: string;
  itemName?: string; // Optional item name to display
}

export function DeleteConfirmationDialog({ 
  isOpen, 
  onOpenChange, 
  onConfirm, 
  onCancel,
  isDeleting = false,
  title,
  description,
  itemName
}: DeleteConfirmationDialogProps) {
  console.log("DeleteConfirmationDialog rendered with props:", { 
    isOpen, isDeleting, title, description, itemName 
  });
  
  const handleConfirm = async (e: React.MouseEvent) => {
    console.log("Delete confirmation button clicked");
    e.preventDefault();
    
    if (isDeleting) {
      console.log("Already deleting, ignoring click");
      return;
    }
    
    try {
      console.log("Starting delete confirmation handler");
      await onConfirm();
      console.log("Delete confirmation handler completed successfully");
      // Note: We don't close the dialog here as that's handled by the parent component
      // after a successful operation, to allow showing success state
    } catch (error) {
      console.error("Error in delete confirmation handler:", error);
      // The dialog will remain open if there's an error
    }
  };

  const handleCancel = () => {
    console.log("Delete operation canceled");
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center">
            <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
            {title}
          </AlertDialogTitle>
          <div className="space-y-2">
            {description.split('\n').map((text, i) => (
              <p key={i} className={i > 0 ? "text-red-500" : "text-muted-foreground"}>
                {text}
              </p>
            ))}
          </div>
          {/* Keep itemName outside of description to format it properly */}
          {itemName && (
            <div className="mt-2 font-medium">
              {itemName}
            </div>
          )}
          {!description.includes("Error:") && (
            <div className="mt-2 text-red-500">
              This action cannot be undone.
            </div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting} onClick={handleCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
